import React, { useCallback, useContext, useRef, useState } from 'react'

import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextInput from '../Form/Input/TextInput'
import FoldableContent from '../FoldableContent/FoldableContent'
import styled from 'styled-components'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'components/Luxkit/Tooltip'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ContactContext from 'contexts/contactContext'
import { redeemGiftCard } from 'api/promo'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import moment from 'moment'
import { DMY_DATE_FORMAT, US_DATE_FORMAT } from 'constants/dateFormats'
import AnnotatedGiftCard from './AnnotatedGiftCard'
import { RedeemedCard } from './common'
import DividerWithText from '../DividerWithText'
import ListItem from 'components/Luxkit/List/ListItem'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import useToggle from 'hooks/useToggle'
import ScanCode from './ScanCode'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import useStateWithRef from 'hooks/useStateWithRef'
import { rem } from 'polished'
import * as Analytics from 'analytics/analytics'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import { isBlackhwakCard } from 'components/Common/RedeemGiftCard/utils'

const StyledGiftGardImage = styled(AnnotatedGiftCard)`
  margin: 0 auto;
  width: 100%;
`

const ScanGiftCardButton = styled(ListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ErrorExampleList = styled.ul`
  margin-top: ${rem(12)};
  margin-bottom: 0;
`

interface Props {
  onRedeem: (redeemedCard: RedeemedCard) => void
  onBack?: () => void
  enableScan?: boolean
}

function EnterCode({ onRedeem, onBack: moveToSummary, enableScan }: Props) {
  const [code, setCode, codeRef] = useStateWithRef('')
  const [pin, setPin, pinRef] = useStateWithRef('')
  const [processing, setProcessing] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('Please check and try again.')
  const { value: isScanMode, on: selectScanMode, off: deselectScanMode } = useToggle()
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [didScanCard, setDidScanCard] = useState(false)
  const didEditManually = useRef(false)

  const { currentRegionCode } = useContext(GeoContext)
  const { defaultContact } = useContext(ContactContext)
  const analyticsPage = useContext(AnalyticsPageContext)

  const shouldAskPin = isBlackhwakCard(code)
  const showSupportNumber = failedAttempts > 2

  const onCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    didEditManually.current = true
  }, [setCode])

  const onPinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value)
    didEditManually.current = true
  }, [setPin])

  const onSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessing(true)
    setHasError(false)

    try {
      const sanitisedCode = codeRef.current.toUpperCase().replace(/[^0-9A-Z]/g, '')
      const redeemResponse = await redeemGiftCard(sanitisedCode, pinRef.current, currentRegionCode)
      const { credits } = redeemResponse.result

      if (didScanCard) {
        if (didEditManually.current) {
          Analytics.trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_modified_from_scan',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        } else {
          Analytics.trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_exact_match',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        }
      }

      onRedeem({
        code: sanitisedCode,
        currencyCode: credits.currency,
        amount: parseFloat(credits.amount),
      })

      const displayCreditAmount = formatDynamicAmountWithDollarType(parseFloat(credits.amount), credits.currency, currentRegionCode)
      const dateFormat = currentRegionCode === 'US' ? US_DATE_FORMAT : DMY_DATE_FORMAT
      const creditExpiry = moment(credits.expires_at).format(dateFormat)

      showSnackbar(
        `${displayCreditAmount} credit has been applied to your account, valid until ${creditExpiry}`,
        'success',
        { heading: 'Gift card redeemed' },
      )
    } catch (e: any) {
      setHasError(true)
      if (e.status === 400) { // display api error message for 400 status
        setErrorMessage(e.message)
      }
      setFailedAttempts(n => n + 1)
    } finally {
      setProcessing(false)
    }
  }, [codeRef, pinRef, didScanCard, onRedeem, currentRegionCode, analyticsPage])

  const onSelectScanMode = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'scan_gift_card',
      action: 'start',
      category: analyticsPage,
      type: 'interaction',
    })
    selectScanMode()
    setHasError(false)
  }, [selectScanMode, analyticsPage])

  const onScanSuccess = useCallback((code: string, pin: string) => {
    setCode(code)
    setPin(pin)
    setDidScanCard(true)
    didEditManually.current = false
    deselectScanMode()
  }, [deselectScanMode, setCode, setPin])

  if (isScanMode) {
    return <ScanCode onSuccess={onScanSuccess} onCancel={deselectScanMode} />
  }

  return (
    <form onSubmit={onSubmit}>
      <ModalHeader title="Redeem gift card" onBackButtonClick={moveToSummary} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="large" as="p">
              Simply enter the voucher code that is located on your printed voucher or email, or the card number on the back of your physical gift card.{' '}
              If a pin is required, a field below ‘Gift card code’ will appear. Once verified we will credit your account with the gifted value.
            </BodyText>
            <TextInput
              label="Gift card code"
              placeholder="Enter code or card number here"
              value={code}
              onChange={onCodeChange}
              disabled={processing}
              manualError
              required
              data-testid="code-field"
            />

            {shouldAskPin &&
              <TextInput
                label={
                  <LabelGroup>
                    {'Access code '}
                    <Tooltip description="Enter the access code that is located in the scratch panel on the back of your physical gift card.">
                      <LineInfoCircleIcon size="XS"/>
                    </Tooltip>
                  </LabelGroup>
                }
                placeholder="e.g. XRNJU13RJL"
                value={pin}
                onChange={onPinChange}
                disabled={processing}
                required
                data-testid="pin-field"
              />}

            {hasError && (
              <MessageBanner kind="critical" heading="Unable to redeem" description={<VerticalSpacer gap={12}>
                {!didScanCard && !showSupportNumber && errorMessage}
                {!didScanCard && showSupportNumber && `${errorMessage}, or call ${defaultContact.humanReadable}`}
                {didScanCard && <>
                  <div>Sometimes the card scanner makes mistakes, such as confusing these characters:</div>
                  <ErrorExampleList>
                    <li>8 and B</li>
                    <li>1 and I</li>
                    <li>Q, G and 6</li>
                  </ErrorExampleList>
                  <div>Please check the details carefully and try again.</div>
                  {showSupportNumber && <div>
                    If you're still having trouble, call us on {defaultContact.humanReadable}.
                  </div>}
                </>}
              </VerticalSpacer>}
              >
              </MessageBanner>
            )}

            <FoldableContent
              openLabel="Where are my card details?"
              closedLabel="Where are my card details?"
              size="medium"
            >
              <BodyText variant="medium">
                For physical cards, the card number is located on the back of your card.
              </BodyText>
              <br/>

              <StyledGiftGardImage />

            </FoldableContent>
          </VerticalSpacer>
          {enableScan && <VerticalSpacer gap={16}>
            <DividerWithText text="or" />

            <ScanGiftCardButton
              title="Scan gift card" size="large"
              startIcon={<LineCameraIcon />}
              endIcon={<LineAngleRightBIcon />}
              onClick={onSelectScanMode}
            />
          </VerticalSpacer>}

        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          type: 'submit',
          children: 'Redeem',
          disabled: processing,
        }}
      />
    </form>
  )
}

export default EnterCode
